import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon, ThermometerIcon, FlameIcon } from '../Icons';
import { TemperatureConverterClient } from './ui/TemperatureConverterClient';

interface TemperatureConverterProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const TemperatureConverter: React.FC<TemperatureConverterProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Ultimate Temperature & Oven Converter (°C, °F, Gas Mark)"
            description="The only tool you need to convert baking temperatures. Instantly switch between Celsius, Fahrenheit, Kelvin, Gas Marks, and Fan Oven adjustments."
            setActiveTool={setActiveTool}
            breadcrumbs={breadcrumbs}
            toolId="temperature-converter"
        >
            <TemperatureConverterClient />
            <TemperatureConverterContent />
        </ToolContainer>
    );
};

const TemperatureConverterContent: React.FC = () => (
    <div className="mt-8 space-y-12 pt-8 border-t border-brand-border text-brand-text-body">
        <div className="prose prose-pink max-w-none">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">Mastering Baking Temperatures</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                Whether you're following a classic British recipe that uses <strong>Gas Marks</strong> or a modern US recipe using <strong>Fahrenheit</strong>, precision is the secret to perfect baking. Our all-in-one converter handles everything from room temperature adjustments to high-heat roasting.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    How to Use
                </h3>
                <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>Enter Temperature:</strong> Type into any of the active fields (Celsius, Fahrenheit, or Kelvin).</li>
                    <li><strong>Instant Conversion:</strong> All other values, including <strong>Gas Mark</strong> and <strong>Fan Oven</strong> adjustment, update instantly.</li>
                    <li><strong>Copy Results:</strong> Use the copy button to save all conversion data for your recipe notes.</li>
                </ol>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Baking Temperature FAQs</h3>
                <div className="space-y-4">
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            What is a Fan Oven adjustment?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Fan (Convection) ovens circulate air, cooking food faster. Typically, you should <strong>reduce the temperature by 20°C (approx 25°F)</strong> compared to a conventional oven recipe.
                        </p>
                    </details>
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            What is Gas Mark 4 in Fahrenheit?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Gas Mark 4 is considered a "Moderate" oven and is equivalent to <strong>350°F or 180°C</strong>.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    </div>
);
